import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    RefreshCw, Download, Printer, ChevronDown, Layers,
    ChevronsDownUp, ChevronsUpDown, Loader2,
} from "lucide-react";
import { ContainerCard } from "../../../components/ui/cards";
import { Button } from "../../../components/ui/button";
import useApiMethods from "../../../lib/useApiMethods";
import apiClient from "../../../lib/apiClient";
import { UNIFIED_REPORT_URL, USERS_SIMPLE_URL } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import { usePersistedState } from "../../../hooks/usePersistedState";
import StateFilterSelect from "../../../components/common/StateFilterSelect";
import { cn } from "../../../utils/tailwindUtils";
import ReportSectionCard from "./ReportSectionCard";
import { DATE_PRESETS, fmtMetric, fmtNumber, resolveRange } from "./formatters";

/**
 * Section catalogue — mirrors `SECTIONS` in `customers/unified_report.py`.
 * Held locally so the page can render its rail and skeletons before any
 * response lands; every payload that arrives replaces its entry.
 */
const SECTION_CATALOG = [
    { key: "pipeline_activity",  label: "Pipeline Activity",     description: "Active deals with value, age and follow-up engagement",     route: "/reports/pipeline-activity" },
    { key: "sales_forecast",     label: "Sales Forecast",        description: "Weighted pipeline forecast and per-deal projection",        route: "/reports/sales-forecast" },
    { key: "conversion",         label: "Conversion Analytics",  description: "Funnel, stage conversion, rep performance and time-to-close", route: "/reports/conversion" },
    { key: "lead_source",        label: "Lead Source",           description: "Lead volume and follow-up health by source",                route: "/reports/lead-types" },
    { key: "lead_meta_form",     label: "Lead Meta Form",        description: "Effectiveness of each lead capture form",                   route: "/reports/lead-sources" },
    { key: "lead_potential",     label: "Lead Potential",        description: "Hot / Medium / Cold split of the open lead base",           route: "/reports/lead-potential" },
    { key: "engagement",         label: "Engagement",            description: "Engagement score and activity recency per customer",        route: "/reports/engagement" },
    { key: "timeline",           label: "Timeline",              description: "Stage-change history with follow-up adherence",             route: "/reports/timeline" },
    { key: "email_analytics",    label: "Email Analytics",       description: "Send, open, click and reply performance",                   route: "/reports/email-analytics" },
    { key: "onboarded_revenue",  label: "Onboarded Revenue",     description: "Contract value, AMC value and renewal rate",                route: "/onboarded/reports/revenue" },
    { key: "implementation",     label: "Implementation Status", description: "Milestone completion and delayed items per client",         route: "/onboarded/reports/implementation" },
    { key: "amc_tracker",        label: "AMC Tracker",           description: "AMC contracts expiring or already overdue",                 route: "/onboarded/reports/amc-tracker" },
];

/** Display order for the executive strip — merged across section responses. */
const KPI_ORDER = [
    "Total Leads", "Open Pipeline", "Weighted Forecast", "Closed This Month",
    "Conversion Rate", "Deals Won", "Hot Leads", "Stale Deals",
    "Email Open Rate", "AMC Overdue", "Onboarded Revenue", "Delayed Milestones",
];

const SCENARIOS = [
    { value: "most_likely", label: "Most Likely" },
    { value: "best_case",   label: "Best Case" },
    { value: "worst_case",  label: "Worst Case" },
];

const ROW_LIMIT = 25;      // rows rendered per section (Excel export carries all)
const CONCURRENCY = 4;     // parallel section requests — keeps each one well inside the API timeout

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #unified-report, #unified-report * { visibility: visible !important; }
  #unified-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
  #unified-report .print\\:hidden { display: none !important; }
}
`;

const selectClass = "appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 " +
    "rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none " +
    "focus:ring-1 focus:ring-indigo-400 cursor-pointer";
const inputClass = "text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 " +
    "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400";

const UnifiedReport = () => {
    const { fetch } = useApiMethods();

    const [datePreset, setDatePreset] = usePersistedState("unified_report_preset", "this_month");
    const [customFrom, setCustomFrom] = usePersistedState("unified_report_from", "");
    const [customTo, setCustomTo]     = usePersistedState("unified_report_to", "");
    const [ownerId, setOwnerId]       = usePersistedState("unified_report_owner", "");
    const [stateFilter, setStateFilter] = usePersistedState("unified_report_state", "");
    const [scenario, setScenario]     = usePersistedState("unified_report_scenario", "most_likely");
    const [amcDays, setAmcDays]       = usePersistedState("unified_report_amc_days", 90);

    const [users, setUsers]       = useState([]);
    const [sections, setSections] = useState({});   // key → normalised section payload
    const [pending, setPending]   = useState([]);   // keys still in flight
    const [kpis, setKpis]         = useState([]);
    const [meta, setMeta]         = useState(null);
    const [allOpen, setAllOpen]   = useState(true);
    const [exporting, setExporting] = useState(false);

    const requestId = useRef(0);

    const rangeReady = datePreset !== "custom" || (customFrom && customTo);

    const query = useMemo(() => {
        const { from_date, to_date } = resolveRange(datePreset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date) params.append("from_date", from_date);
        if (to_date)   params.append("to_date", to_date);
        if (ownerId)   params.append("assigned_user_id", ownerId);
        if (stateFilter) params.append("state", stateFilter);
        params.append("scenario", scenario);
        params.append("amc_days", amcDays);
        return params;
    }, [datePreset, customFrom, customTo, ownerId, stateFilter, scenario, amcDays]);

    /**
     * Each section is fetched on its own so a slow report renders late instead of
     * holding up the page — the user still sees one report, one filter set.
     */
    const load = useCallback(async () => {
        if (!rangeReady) return;
        const runId = ++requestId.current;

        setSections({});
        setKpis([]);
        setMeta(null);
        setPending(SECTION_CATALOG.map(s => s.key));

        const queue = [...SECTION_CATALOG];

        const worker = async () => {
            while (queue.length) {
                const spec = queue.shift();
                const params = new URLSearchParams(query);
                params.append("sections", spec.key);
                params.append("row_limit", ROW_LIMIT);

                let res;
                try {
                    res = await fetch(`${UNIFIED_REPORT_URL}?${params.toString()}`);
                } catch {
                    res = null;
                }
                if (runId !== requestId.current) return;   // filters changed — drop stale result

                const payload = res?.status === "success" ? res.sections?.[spec.key] : null;
                setSections(prev => ({
                    ...prev,
                    [spec.key]: payload || {
                        ...spec,
                        status: "error",
                        message: res?.message || "This report could not be loaded",
                        summary: [], columns: [], rows: [], total_rows: 0, extra: {},
                    },
                }));
                if (res?.kpis?.length) {
                    setKpis(prev => {
                        const merged = [...prev];
                        res.kpis.forEach(k => {
                            if (!merged.some(existing => existing.label === k.label)) merged.push(k);
                        });
                        return merged;
                    });
                }
                if (res?.meta) setMeta(res.meta);
                setPending(prev => prev.filter(key => key !== spec.key));
            }
        };

        await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    }, [query, rangeReady]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL)
            .then(res => { if (res?.status === "success") setUsers(res.data || []); })
            .catch(() => {});
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    /** One workbook: Executive Summary + a sheet per report, full row set. */
    const downloadExcel = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams(query);
            params.append("export_type", "excel");
            const data = await apiClient.get(`${UNIFIED_REPORT_URL}?${params.toString()}`, {
                responseType: "blob",
                timeout: 300000,
            });
            const blob = data instanceof Blob
                ? data
                : new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `All_In_One_Sales_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            ToastMsg("error", "Could not generate the Excel export");
        } finally {
            setExporting(false);
        }
    };

    const orderedKpis = useMemo(() => {
        const rank = label => {
            const i = KPI_ORDER.indexOf(label);
            return i === -1 ? KPI_ORDER.length : i;
        };
        return [...kpis].sort((a, b) => rank(a.label) - rank(b.label));
    }, [kpis]);

    const visibleSections = useMemo(
        () => SECTION_CATALOG.filter(spec => sections[spec.key]?.status !== "denied"),
        [sections]
    );
    const deniedCount = SECTION_CATALOG.length - visibleSections.length;
    const loadedCount = SECTION_CATALOG.length - pending.length;
    const loading = pending.length > 0;

    const jumpTo = (key) => {
        const el = document.getElementById(`section-${key}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const doPrint = () => {
        setAllOpen(true);
        setTimeout(() => window.print(), 250);
    };

    const rangeLabel = useMemo(() => {
        const { from_date, to_date } = resolveRange(datePreset, customFrom, customTo);
        if (!from_date || !to_date) return "All time";
        return `${from_date} → ${to_date}`;
    }, [datePreset, customFrom, customTo]);

    return (
        <ContainerCard>
            <style>{PRINT_CSS}</style>

            <div id="unified-report" className="flex flex-col min-h-0 flex-1">
                {/* ── header ── */}
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3 px-1">
                    <div className="flex items-start gap-2">
                        <Layers className="h-5 w-5 mt-0.5 text-sidebar-primary" />
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                All-in-One Sales Report
                            </h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Every sales report on one page — {SECTION_CATALOG.length} sections, one set of filters,
                                one export. Period: <span className="font-medium">{rangeLabel}</span>
                                {meta?.generated_at && ` · generated ${meta.generated_at}`}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 print:hidden">
                        <Button onClick={() => setAllOpen(v => !v)} size="sm" variant="outline" className="text-xs gap-1">
                            {allOpen ? <ChevronsDownUp className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3" />}
                            {allOpen ? "Collapse all" : "Expand all"}
                        </Button>
                        <Button onClick={doPrint} size="sm" variant="outline" className="text-xs gap-1">
                            <Printer className="h-3 w-3" /> Print / PDF
                        </Button>
                        <Button onClick={downloadExcel} size="sm" variant="outline" className="text-xs gap-1" disabled={exporting || loading}>
                            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            {exporting ? "Preparing…" : "Download Excel"}
                        </Button>
                        <Button onClick={load} size="sm" variant="outline" className="text-xs gap-1" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                    </div>
                </div>

                {/* ── shared filter bar ── */}
                <div className="flex flex-wrap items-center gap-2 mb-3 px-1 print:hidden">
                    <div className="relative">
                        <select
                            value={datePreset}
                            onChange={e => {
                                setDatePreset(e.target.value);
                                if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                            }}
                            className={selectClass}
                        >
                            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    {datePreset === "custom" && (
                        <>
                            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className={inputClass} />
                            <span className="text-xs text-gray-400">to</span>
                            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className={inputClass} />
                        </>
                    )}

                    <div className="relative">
                        <select value={ownerId} onChange={e => setOwnerId(e.target.value)} className={selectClass}>
                            <option value="">All Reps</option>
                            {users.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <StateFilterSelect value={stateFilter} onChange={setStateFilter} />

                    <div className="relative">
                        <select value={scenario} onChange={e => setScenario(e.target.value)} className={selectClass} title="Sales forecast scenario">
                            {SCENARIOS.map(s => <option key={s.value} value={s.value}>Forecast: {s.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <div className="relative">
                        <select value={amcDays} onChange={e => setAmcDays(Number(e.target.value))} className={selectClass} title="AMC expiry window">
                            {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>AMC window: {d}d</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <span className="ml-auto text-[10px] text-gray-500 dark:text-gray-400">
                        {loading
                            ? `Loading ${loadedCount}/${SECTION_CATALOG.length} reports…`
                            : `${visibleSections.length} reports loaded`}
                        {deniedCount > 0 && !loading && ` · ${deniedCount} hidden (no access)`}
                    </span>
                </div>

                {/* ── executive KPI strip ── */}
                {orderedKpis.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3 px-1">
                        {orderedKpis.map(k => (
                            <div
                                key={k.label}
                                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 px-3 py-2.5"
                            >
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
                                    {k.label}
                                </p>
                                <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                                    {fmtMetric(k.value, k.format, { compact: true })}
                                </p>
                                {k.hint && (
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{k.hint}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── jump rail ── */}
                <div className="flex flex-wrap gap-1.5 mb-3 px-1 print:hidden">
                    {visibleSections.map((spec, idx) => {
                        const section = sections[spec.key];
                        const isPending = pending.includes(spec.key);
                        return (
                            <button
                                key={spec.key}
                                onClick={() => jumpTo(spec.key)}
                                className={cn(
                                    "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors",
                                    "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700",
                                    "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                )}
                            >
                                <span className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    isPending ? "bg-gray-300 animate-pulse"
                                        : section?.status === "success" ? "bg-emerald-500"
                                        : "bg-amber-500"
                                )} />
                                {idx + 1}. {spec.label}
                                {section?.status === "success" && section.total_rows > 0 && (
                                    <span className="text-gray-400">· {fmtNumber(section.total_rows)}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* ── sections ── */}
                <div className="flex-1 min-h-0 overflow-y-auto space-y-3 px-1 pb-4 print:overflow-visible">
                    {visibleSections.map((spec, idx) => (
                        <ReportSectionCard
                            key={`${spec.key}-${allOpen}`}
                            index={idx + 1}
                            section={sections[spec.key] || { ...spec, status: "success", summary: [], columns: [], rows: [], total_rows: 0, extra: {} }}
                            loading={pending.includes(spec.key)}
                            defaultOpen={allOpen}
                        />
                    ))}

                    {!rangeReady && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 px-1">
                            Pick both a start and an end date to run the report.
                        </p>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
};

export default UnifiedReport;
